import { getStrikeStepForSymbol } from '../config/constants'
import { AnalysisMode, type AnalysisResult, type OptionData, type VarianceResult } from '../types/index'
import { calculateDynamicVariance, calculateSupportResistance } from './analytics'
import { getSortedOptions } from './core-utils'

export const STRIKE_LABELS = {
    RESISTANCE: 'R',
    RESISTANCE_2: 'R1',
    SUPPORT: 'S',
    SUPPORT_2: 'S1',
    SHIFT: 'Sh',
} as const

export const LABEL_CLASSES = {
    RESISTANCE:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-rose-200 text-[9px] font-black text-rose-600 shadow-sm z-10 ring-1 ring-rose-50',
    RESISTANCE_2:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-rose-200 text-[9px] font-black text-rose-600 shadow-sm z-10 ring-1 ring-rose-50',
    SUPPORT:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-emerald-200 text-[9px] font-black text-emerald-600 shadow-sm z-10 ring-1 ring-emerald-50',
    SUPPORT_2:
        'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-emerald-200 text-[9px] font-black text-emerald-600 shadow-sm z-10 ring-1 ring-emerald-50',
    SHIFT: 'absolute -top-2.5 -right-1.5 flex items-center justify-center w-5 h-5 rounded-full bg-white border border-indigo-200 text-[9px] font-black text-indigo-600 shadow-sm z-10 ring-1 ring-indigo-50',
} as const

// Re-export specific analytics functions for external use (e.g. useAnalysisHistory)
export { calculateSupportResistance, calculateDynamicVariance as calculateVarianceMetrics, calculateDynamicVariance }
export { getSortedOptions } // Re-export for compatibility

export type StrategyType = 'Straddle' | 'Strangle' | 'IronFly'

export interface StrategyLeg {
    type: 'CE' | 'PE'
    action: 'BUY' | 'SELL'
    strike: number
    price: number
}

export interface StrategyResult {
    id: string
    name: string
    type: StrategyType
    netPremium: number
    maxProfit: number // or Infinity
    maxLoss: number
    breakevenLower: number
    breakevenUpper: number
    pop: number // Probability of Profit (mocked for now)
    riskRewardRatio: number
    delta: number
    theta: number
    gamma: number
    vega: number
    legs: StrategyLeg[]
}

export const getStrikeStep = (symbol: string, options: OptionData[]): number => {
    // 1. Use centralized configuration (Handles aliases like 'Nifty Bank')
    if (symbol) {
        return getStrikeStepForSymbol(symbol)
    }

    // 2. Fallback: Dynamic calculation from data (Crash proof)
    if (options && options.length > 1) {
        const opt1 = options[1]
        const opt0 = options[0]
        if (opt1 && opt0) {
            const diff = opt1.strike - opt0.strike
            return diff || 50
        }
    }
    return 50
}

const analysisCache = new Map<string, any>()

export const calculateAnalysis = (
    data: OptionData[],
    range: number,
    spotPrice: number,
    mode: AnalysisMode,
    symbol?: string,
    baseOptions?: OptionData[],
    baseSpot: number = 0, // NEW PARAM
    disableFallback: boolean = false, // NEW PARAM
    includeStrikes: number[] = [], // NEW PARAM: Force include these strikes
): AnalysisResult & { variance: VarianceResult | null; atmStrike: number } => {
    // CACHE KEY: Use content fingerprint, NOT timestamp (timestamp changes every tick causing flickering)
    // Fingerprint: sum of top 5 CE+PE OI values — changes only when actual data changes
    let fingerprint = 0
    const sampleCount = Math.min(5, data.length)
    for (let i = 0; i < sampleCount; i++) {
        const d = data[i]
        if (d) fingerprint += (d.callOpenInterest || 0) + (d.putOpenInterest || 0)
    }
    const cacheKey = `${symbol}-${mode}-${range}-${Math.round(spotPrice)}-${data.length}-${fingerprint}-${Math.round(baseSpot)}-${disableFallback}`

    if (analysisCache.has(cacheKey)) {
        return analysisCache.get(cacheKey)
    }

    // Use helper for consistent step calculation
    const step = getStrikeStep(symbol || '', data)
    const atm = Math.round(spotPrice / step) * step

    // 1. Get Support, Resistance, Shift, PCR from Centralized Analytics
    const {
        support,
        resistance,
        support2,
        resistance2,
        shift,
        shiftOld,
        pcr,
        pcrOI,
        pcrVolume,
        maxPainStrike,
        maxCallOI,
        maxPutOI,
        ceOtmStrike,
        peOtmStrike,
        shiftV,
        shiftV_OI,
        shiftV_Vol,
        v1Strike,
        v2Strike,
        v3Strike,
        v1Vol,
        v2Vol,
        v3Vol,
        totalCallOI,
        totalPutOI,
    } = calculateSupportResistance(
        data,
        spotPrice,
        mode === AnalysisMode.OI ? 'OI' : 'VOLUME',
        range,
        (symbol as any) || 'NIFTY',
        baseOptions, // Pass base options
        baseSpot, // Pass base spot
    )

    // 2. Calculate Variance (Delegates to Analytics + Rank Logic)
    // Note: We could potentially extract this from step 1 if calculateSupportResistance returned full variance ref.
    // For now, keep as explicit separate call for clarity.
    const variance = calculateDynamicVariance(
        data,
        atm,
        range,
        step,
        mode,
        baseOptions,
        baseSpot, // Pass baseSpot
        disableFallback, // Pass disableFallback
    )

    // 3. Prepare Data for Table Rendering (Rows with Labels)
    // Filter range: ATM ± range strikes for visual display OR explicitly included strikes
    let filtered = data.filter((d) => {
        if (includeStrikes.includes(d.strike)) return true // Force include

        const strikeDiff = Math.abs(d.strike - atm)
        const strikeCount = strikeDiff / step
        return strikeCount === 0 || strikeCount <= range
    })

    // Ensure we have sorted list around ATM
    let atmStrikeObj = data.find((d) => d.strike === atm)
    if (!atmStrikeObj && data.length > 0) {
        atmStrikeObj = data.reduce((prev, curr) =>
            Math.abs(curr.strike - atm) < Math.abs(prev.strike - atm) ? curr : prev,
        )
    }
    const anchorStrike = atmStrikeObj?.strike || atm
    const belowATM = data
        .filter((d) => d.strike < anchorStrike)
        .sort((a, b) => b.strike - a.strike)
        .slice(0, range)
    const aboveATM = data
        .filter((d) => d.strike > anchorStrike)
        .sort((a, b) => a.strike - b.strike)
        .slice(0, range)

    filtered = [...belowATM.reverse(), ...(atmStrikeObj ? [atmStrikeObj] : []), ...aboveATM]

    // Identify S2 and R2 for labels - Now taken DIRECTLY from analytics result
    const resistanceStrike2 = resistance2
    const supportStrike2 = support2

    // Centralized Table Row Generation
    const tableData = filtered.map((opt) => {
        let label = ''
        let labelClass = ''

        if (opt.strike === resistance) {
            label = STRIKE_LABELS.RESISTANCE
            labelClass = LABEL_CLASSES.RESISTANCE
        } else if (opt.strike === support) {
            label = STRIKE_LABELS.SUPPORT
            labelClass = LABEL_CLASSES.SUPPORT
        } else if (opt.strike === resistanceStrike2 && resistanceStrike2 !== resistance) {
            label = STRIKE_LABELS.RESISTANCE_2
            labelClass = LABEL_CLASSES.RESISTANCE_2
        } else if (opt.strike === supportStrike2 && supportStrike2 !== support) {
            label = STRIKE_LABELS.SUPPORT_2
            labelClass = LABEL_CLASSES.SUPPORT_2
        }

        const val =
            mode === AnalysisMode.OI ? opt.callOpenInterest + opt.putOpenInterest : opt.callVolume + opt.putVolume

        return {
            ...opt,
            label,
            labelClass,
            sortValue: val,
            shiftValue: shift,
            strike: opt.strike,
        }
    })

    // Ranks
    // Ranks - Use Centralized Sorter
    const sortedForRank = [...tableData].sort((a, b) => b.sortValue - a.sortValue)
    const rankMap = new Map()
    sortedForRank.forEach((item, i) => {
        rankMap.set(item.strike, i + 1)
    })

    // CE Ranks
    const sortedForCeRank = getSortedOptions(tableData, mode, 'CE')
    const ceRankMap = new Map()
    sortedForCeRank.forEach((item, i) => {
        ceRankMap.set(item.strike, i + 1)
    })

    // PE Ranks
    const sortedForPeRank = getSortedOptions(tableData, mode, 'PE')
    const peRankMap = new Map()
    sortedForPeRank.forEach((item, i) => {
        peRankMap.set(item.strike, i + 1)
    })

    const finalRows = tableData.map((row) => ({
        ...row,
        rank: rankMap.get(row.strike) || 0,
        ceRank: ceRankMap.get(row.strike) || 0,
        peRank: peRankMap.get(row.strike) || 0,
    }))

    const result = {
        resistanceStrike: resistance,
        resistanceStrike2,
        supportStrike: support,
        supportStrike2,
        shift,
        shiftOld: shiftOld ?? shift,
        pcr,
        pcrOI,
        pcrVolume,
        maxPainStrike,
        maxCallOI,
        maxPutOI,
        maxCallVol: data.length > 0 ? data.reduce((max, o) => Math.max(max, o.callVolume || 0), 0) : 0,
        maxPutVol: data.length > 0 ? data.reduce((max, o) => Math.max(max, o.putVolume || 0), 0) : 0,
        variance,
        atmStrike: atm,
        ceOtmStrike: ceOtmStrike || 0,
        peOtmStrike: peOtmStrike || 0,
        shiftV,
        shiftV_OI,
        shiftV_Vol,
        v1Strike,
        v2Strike,
        v3Strike,
        v1Vol,
        v2Vol,
        v3Vol,
        totalCallOI: totalCallOI || 0,
        totalPutOI: totalPutOI || 0,
        tableRows: finalRows,
    }

    // Cache limit to prevent memory leak
    if (analysisCache.size > 50) analysisCache.clear()
    analysisCache.set(cacheKey, result)

    return result
}

/**
 * High-performance, vectorized calculation of core support/resistance/variance metrics.
 * Skips all DOM table row construction, filtering, and string formatting.
 * Used by Web Workers and high-throughput analytical loops for maximum speed.
 */
export const calculateAnalysisFast = (
    data: OptionData[],
    range: number,
    spotPrice: number,
    mode: AnalysisMode,
    symbol?: string,
    baseOptions?: OptionData[],
    baseSpot: number = 0,
    disableFallback: boolean = false,
) => {
    const step = getStrikeStep(symbol || '', data)
    const atm = Math.round(spotPrice / step) * step

    const sr = calculateSupportResistance(
        data,
        spotPrice,
        mode === AnalysisMode.OI ? 'OI' : 'VOLUME',
        range,
        (symbol as any) || 'NIFTY',
        baseOptions,
        baseSpot,
    )

    const variance = calculateDynamicVariance(
        data,
        atm,
        range,
        step,
        mode,
        baseOptions,
        baseSpot,
        disableFallback,
    )

    return {
        supportStrike: sr.support,
        resistanceStrike: sr.resistance,
        supportStrike2: sr.support2,
        resistanceStrike2: sr.resistance2,
        shift: sr.shift,
        shiftOld: sr.shiftOld,
        shiftV: sr.shiftV,
        shiftV_OI: sr.shiftV_OI,
        shiftV_Vol: sr.shiftV_Vol,
        pcr: sr.pcr,
        pcrOI: sr.pcrOI,
        pcrVolume: sr.pcrVolume,
        maxPainStrike: sr.maxPainStrike,
        maxCallOI: sr.maxCallOI,
        maxPutOI: sr.maxPutOI,
        maxCallVol: (sr as any).maxCallVol,
        maxPutVol: (sr as any).maxPutVol,
        variance,
        atmStrike: atm,
        ceOtmStrike: sr.ceOtmStrike,
        peOtmStrike: sr.peOtmStrike,
        v1Strike: sr.v1Strike,
        v2Strike: sr.v2Strike,
        v3Strike: sr.v3Strike,
        v1Vol: sr.v1Vol,
        v2Vol: sr.v2Vol,
        v3Vol: sr.v3Vol,
        totalCallOI: sr.totalCallOI || 0,
        totalPutOI: sr.totalPutOI || 0,
    }
}

export function analyzeExpandedHistory(
    history: any[],
    symbol: string,
    range: number,
    mode: AnalysisMode,
    _currentSpot: number,
) {
    const defaults = {
        open: 'N/A',
        high: 'N/A',
        low: 'N/A',
        trend5m: 'N/A',
        shiftTrend: 'Stable',
        varianceTrend: 'Stable',
        divergence: 'None',
    }

    if (!history || history.length === 0) return defaults

    const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp)

    const prices = sorted.map((h) => h.indexData?.[symbol]?.lastPrice).filter((p) => p)
    if (prices.length === 0) return defaults

    const open = prices[0]
    const high = prices.length > 0 ? prices.reduce((max, p) => Math.max(max, p), -Infinity) : 'N/A'
    const low = prices.length > 0 ? prices.reduce((min, p) => Math.min(min, p), Infinity) : 'N/A'
    const current = prices[prices.length - 1]

    const lookback = Math.min(5, prices.length - 1)
    const prevPrice = prices[prices.length - 1 - lookback] || open
    const priceDiff = current - prevPrice
    let trend5m = 'Sideways'
    if (priceDiff > current * 0.0005) trend5m = 'Up Trend ↗️'
    else if (priceDiff < -(current * 0.0005)) trend5m = 'Down Trend ↘️'

    const sampleIndices = []
    const len = sorted.length
    if (len > 0) sampleIndices.push(0)
    if (len > 20) sampleIndices.push(Math.floor(len / 2))
    for (let i = Math.max(0, len - 5); i < len; i++) {
        if (!sampleIndices.includes(i)) sampleIndices.push(i)
    }
    sampleIndices.sort((a, b) => a - b)

    const trendPoints = sampleIndices
        .map((idx) => {
            const snap = sorted[idx]
            if (!snap.optionsData || !snap.optionsData[symbol]) return null
            const symbolData = snap.optionsData[symbol]
            const opts = (Object.values(symbolData)[0] || []) as any[]

            let snapSpot = snap.indexData?.[symbol]?.lastPrice
            if (!snapSpot && opts.length > 0) snapSpot = opts[0].strike
            if (!snapSpot) return null

            const res = calculateAnalysis(opts, range, snapSpot, mode, symbol)
            return {
                timestamp: snap.timestamp,
                shift: res.shift,
                variance: res.variance?.finalVariance || 0,
                price: snapSpot,
            }
        })
        .filter(Boolean) as any[]

    if (trendPoints.length < 2) return { ...defaults, open, high, low, trend5m }

    const startPoint = trendPoints[0]
    const endPoint = trendPoints[trendPoints.length - 1]
    const shiftChange = endPoint.shift - startPoint.shift
    let shiftTrend = 'Stable'
    if (shiftChange > 5) shiftTrend = `Rising ↗️ (+${shiftChange.toFixed(0)})`
    else if (shiftChange < -5) shiftTrend = `Falling ↘️ (${shiftChange.toFixed(0)})`

    const varChange = endPoint.variance - startPoint.variance
    let varianceTrend = 'Stable'
    if (varChange > 2) varianceTrend = `Rising ↗️ (Active Buying)`
    else if (varChange < -2) varianceTrend = `Falling ↘️ (Active Selling)`

    const recentPoints = trendPoints.slice(-3)
    if (recentPoints.length >= 2) {
        const last = recentPoints[recentPoints.length - 1]
        const prev = recentPoints[0]
        if (prev.variance < 0 && last.variance > 0) varianceTrend = '🌊 BULLISH REVERSAL (Flipped to +ve)'
        if (prev.variance > 0 && last.variance < 0) varianceTrend = '🌊 BEARISH REVERSAL (Flipped to -ve)'
    }

    let divergence = 'None'
    if ((trend5m.includes('Down') || trend5m === 'Sideways') && varianceTrend.includes('Rising')) {
        divergence = '🐂 BULLISH DIVERGENCE (Price Down, Flow Up)'
    } else if ((trend5m.includes('Up') || trend5m === 'Sideways') && varianceTrend.includes('Falling')) {
        divergence = '🐻 BEARISH DIVERGENCE (Price Up, Flow Down)'
    }

    return { open, high, low, trend5m, shiftTrend, varianceTrend, divergence }
}

export const calculateStrategyMetrics = (
    options: OptionData[],
    selectedStrikes: number[],
    type: StrategyType,
    _width: number,
): StrategyResult[] => {
    if (options.length === 0 || selectedStrikes.length === 0) return []

    const results: StrategyResult[] = []

    selectedStrikes.forEach((strike) => {
        const atm = options.find((o) => o.strike === strike)
        if (!atm) return

        let netPremium = 0
        const legs: StrategyLeg[] = []

        // Helper to find closest strike
        const getOption = (targetStrike: number, type: 'CE' | 'PE') => {
            return options.reduce((prev, curr) =>
                Math.abs(curr.strike - targetStrike) < Math.abs(prev.strike - targetStrike) ? curr : prev,
            )
        }

        const width = _width > 0 ? _width : 200 // Default width if 0

        if (type === 'Straddle') {
            // Long Straddle: Buy ATM CE + PE
            legs.push({ type: 'CE', action: 'BUY', strike: atm.strike, price: atm.callPrice })
            legs.push({ type: 'PE', action: 'BUY', strike: atm.strike, price: atm.putPrice })
        } else if (type === 'Strangle') {
            // Long Strangle: Buy OTM CE + PE
            const ceOption = getOption(strike + width, 'CE')
            const peOption = getOption(strike - width, 'PE')

            legs.push({ type: 'CE', action: 'BUY', strike: ceOption.strike, price: ceOption.callPrice })
            legs.push({ type: 'PE', action: 'BUY', strike: peOption.strike, price: peOption.putPrice })
        } else if (type === 'IronFly') {
            // Long Iron Fly: Buy ATM Straddle, Sell OTM Wings
            // Buy Body
            legs.push({ type: 'CE', action: 'BUY', strike: atm.strike, price: atm.callPrice })
            legs.push({ type: 'PE', action: 'BUY', strike: atm.strike, price: atm.putPrice })

            // Sell Wings
            const ceWing = getOption(strike + width, 'CE')
            const peWing = getOption(strike - width, 'PE')

            legs.push({ type: 'CE', action: 'SELL', strike: ceWing.strike, price: ceWing.callPrice })
            legs.push({ type: 'PE', action: 'SELL', strike: peWing.strike, price: peWing.putPrice })
        }

        // Calculate Net Premium from Legs
        netPremium = legs.reduce((sum, leg) => (leg.action === 'BUY' ? sum + leg.price : sum - leg.price), 0)

        const breakevenLower = strike - netPremium
        const breakevenUpper = strike + netPremium
        const maxLoss = netPremium * 50 // Approximation
        let maxProfit = Infinity

        if (type === 'IronFly') {
            // Iron Fly (Long) is Capped Profit.
            // Profit = (Width - Net Debit)
            maxProfit = (width - netPremium) * 50 // Approx
        }

        results.push({
            id: `${type}-${strike}`,
            name: `${type} @ ${strike}`,
            type: type,
            netPremium: netPremium,
            maxProfit: maxProfit,
            maxLoss: maxLoss,
            breakevenLower,
            breakevenUpper,
            pop: 0.55,
            riskRewardRatio: 1.5,
            delta: (atm.callDelta || 0) + (atm.putDelta || 0),
            theta: (atm.callTheta || 0) + (atm.putTheta || 0),
            gamma: (atm.callGamma || 0) + (atm.putGamma || 0),
            vega: (atm.callVega || 0) + (atm.putVega || 0),
            legs: legs,
        })
    })

    return results
}

export const calculateRSI = (prices: number[], period: number = 14): number[] => {
    if (prices.length < period + 1) return new Array(prices.length).fill(NaN)

    const rsiArray: number[] = []
    let gains = 0
    let losses = 0

    for (let i = 1; i <= period; i++) {
        const p1 = prices[i]
        const p0 = prices[i - 1]
        if (p1 !== undefined && p0 !== undefined) {
            const diff = p1 - p0
            if (diff >= 0) gains += diff
            else losses += Math.abs(diff)
        }
    }

    let avgGain = gains / period
    let avgLoss = losses / period

    let rs = avgLoss === 0 ? 100 : avgGain / avgLoss
    let rsi = 100 - 100 / (1 + rs)

    for (let i = 0; i < period; i++) rsiArray.push(NaN)
    rsiArray.push(rsi)

    for (let i = period + 1; i < prices.length; i++) {
        const p1 = prices[i]
        const p0 = prices[i - 1]
        if (p1 !== undefined && p0 !== undefined) {
            const diff = p1 - p0
            const gain = diff >= 0 ? diff : 0
            const loss = diff < 0 ? Math.abs(diff) : 0

            avgGain = (avgGain * (period - 1) + gain) / period
            avgLoss = (avgLoss * (period - 1) + loss) / period

            rs = avgLoss === 0 ? 100 : avgGain / avgLoss
            rsi = 100 - 100 / (1 + rs)
            rsiArray.push(rsi)
        }
    }

    while (rsiArray.length < prices.length) rsiArray.unshift(NaN)

    return rsiArray
}
